// src/components/CMS/AI/AIGenerateButton.jsx

import { useState, useEffect } from 'react';
import { Sparkles, Loader2, Wand2 } from 'lucide-react';
import useCmsStore from '../../../services/useCmsStore';
import './AIGenerateButton.css';

/**
 * AI Generate Button - Shows only when AI credentials are configured.
 * Allows generating content from just the title.
 *
 * @param {Object} props
 * @param {string} props.title - Current title value
 * @param {string} props.type - 'article' or 'page'
 * @param {string} props.language - Content language (default: 'pt-BR')
 * @param {string} props.pageType - For pages: 'page', 'landing', 'services', etc.
 * @param {Function} props.onGenerated - Callback with generated content
 */
const AIGenerateButton = ({
  title,
  type = 'article',
  language = 'pt-BR',
  pageType = 'page',
  onGenerated
}) => {
  const {
    aiAvailable,
    aiGenerating,
    checkAIStatus,
    generateArticleWithAI,
    generatePageWithAI,
  } = useCmsStore();

  const [checked, setChecked] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  // Check AI status on mount
  useEffect(() => {
    if (!checked) {
      checkAIStatus().then(() => setChecked(true));
    }
  }, [checked, checkAIStatus]);

  // Don't render if AI is not available
  if (!checked || !aiAvailable) {
    return null;
  }

  const handleGenerate = async () => {
    if (!title || title.trim().length < 3) {
      alert('Digite um título com pelo menos 3 caracteres para gerar conteúdo com IA.');
      return;
    }

    try {
      let result;
      if (type === 'article') {
        result = await generateArticleWithAI(title.trim(), language);
      } else {
        result = await generatePageWithAI(title.trim(), language, pageType);
      }

      if (onGenerated && result) {
        onGenerated(result);
      }
    } catch (error) {
      console.error('AI generation error:', error);
      alert('Erro ao gerar conteúdo com IA. Verifique as credenciais.');
    }
  };

  return (
    <div className="ai-generate-wrapper">
      <button
        type="button"
        className={`ai-generate-button ${aiGenerating ? 'generating' : ''}`}
        onClick={handleGenerate}
        disabled={aiGenerating || !title || title.trim().length < 3}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        {aiGenerating ? (
          <>
            <Loader2 size={16} className="spin" />
            <span>Gerando...</span>
          </>
        ) : (
          <>
            <Sparkles size={16} />
            <span>Gerar com IA</span>
          </>
        )}
      </button>

      {showTooltip && !aiGenerating && (
        <div className="ai-generate-tooltip">
          {title && title.trim().length >= 3
            ? `Gerar ${type === 'article' ? 'artigo completo' : 'conteúdo da página'} baseado no título`
            : 'Digite um título para gerar conteúdo'}
        </div>
      )}
    </div>
  );
};

/**
 * AI Actions Dropdown - For text improvement actions
 */
export const AIActionsDropdown = ({
  selectedText,
  language = 'pt-BR',
  onImproved
}) => {
  const { aiAvailable, aiGenerating, checkAIStatus, improveTextWithAI } = useCmsStore();
  const [checked, setChecked] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!checked) {
      checkAIStatus().then(() => setChecked(true));
    }
  }, [checked, checkAIStatus]);

  if (!checked || !aiAvailable || !selectedText) {
    return null;
  }

  const actions = [
    { id: 'improve', label: 'Melhorar texto', icon: Wand2 },
    { id: 'expand', label: 'Expandir texto', icon: Sparkles },
    { id: 'summarize', label: 'Resumir texto', icon: Sparkles },
    { id: 'fix_grammar', label: 'Corrigir gramática', icon: Sparkles },
  ];

  const handleAction = async (action) => {
    try {
      const result = await improveTextWithAI(selectedText, action, language);
      if (onImproved && result?.text) {
        onImproved(result.text);
      }
      setIsOpen(false);
    } catch (error) {
      console.error('AI improve error:', error);
    }
  };

  return (
    <div className="ai-actions-dropdown">
      <button
        type="button"
        className="ai-actions-trigger"
        onClick={() => setIsOpen(!isOpen)}
        disabled={aiGenerating}
      >
        <Sparkles size={14} />
        <span>IA</span>
      </button>

      {isOpen && (
        <div className="ai-actions-menu">
          {actions.map(action => (
            <button
              key={action.id}
              type="button"
              className="ai-action-item"
              onClick={() => handleAction(action.id)}
              disabled={aiGenerating}
            >
              <action.icon size={14} />
              <span>{action.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default AIGenerateButton;
